import {
  Machine,
  actions,
  send,
  DoneInvokeEvent,
  forwardTo,
} from 'xstate';
import { assign } from '@xstate/immer';
import cogoToast from 'cogo-toast';

import { PageInfo } from '~/api/models/leadads/subscription';
import { Form } from '~/api/models/leadads/form';
import { checkTosAccepted } from '~/api/routes/leadad/subscription';

import { sendErrorService } from '~/services/error';

import subscriptionMachine, {
  SubscriptionEvent,
  InitEvent,
} from './subscription';
import tosMachine from './tosMachine';

export interface LeadAdContext {
  formsExist: boolean;
  type: string,
  source: string;
  enabledByDefault: boolean;
  pageInfo: PageInfo;
  creatorId: string;
  accountId: string;
  userId: string;
  selectedForm: string | null;
  dnsVerified: boolean;
}

export interface LeadAdSchema {
  states: {
    initializing: {
      states: {
        userId: {
          states: {
            awaiting: {};
            ready: {};
          };
        };
        accountId: {
          states: {
            awaiting: {};
            ready: {};
          };
        };
        pageInfo: {
          states: {
            awaiting: {};
            ready: {};
          };
        };
        creatorId: {
          states: {
            awaiting: {};
            ready: {};
          };
        };
      };
    };
    hidden: {};
    disabled: {
      states: {
        checkingTosAccepted: {};
        needsTos: {};
        idle: {};
      };
    };
    enabled: {
      states: {
        loadingFormData: {};
        formSelected: {};
        select: {};
        create: {
          states: {
            idle: {};
            first: {};
          };
        };
      }
    };
  };
}

export type LeadAdEvent =
  | { type: 'HIDE' }
  | { type: 'SHOW' }
  | { type: 'TOGGLE_ENABLED' }
  | { type: 'MODAL.open' }
  | { type: 'PAGE_INFO.change'; pageInfo: PageInfo }
  | { type: 'ACCOUNT_ID.change'; id: string }
  | { type: 'CREATOR_ID.change'; id: string }
  | { type: 'USER_ID.change'; id: string }
  | { type: 'TYPES.change'; leadType: string }
  | { type: 'SOURCES.change'; source: string }
  | { type: 'FORMS.noForms'; }
  | { type: 'FORMS.retrieved' }
  | { type: 'FORMS.select'; id: string }
  | { type: 'FORMS.reset' }
  | { type: 'FORMS.created'; form: Form }
  | { type: 'FORM_MANAGER.toggleModes' }
  | { type: 'DNS.verified' }
  | { type: 'SUBSCRIPTION.notConnected' }
  | { type: 'SUBSCRIPTION.connected'; leadType?: string };

const { pure } = actions;

// Action Creators

const initSubscription = (ctx: LeadAdContext): InitEvent => ({
  type: 'SUBSCRIPTION.init',
  pageId: ctx.pageInfo.id,
  accountId: ctx.accountId,
});

const createSubscription = (ctx: LeadAdContext): SubscriptionEvent => ({
  type: 'SUBSCRIPTION.create',
  subscription: {
    accountId: ctx.accountId,
    pageInfo: ctx.pageInfo,
    creatorId: ctx.creatorId,
    userId: ctx.userId,
    leadType: ctx.type,
  },
});

const resetSubscription = (ctx: LeadAdContext): SubscriptionEvent => ({
  type: 'SUBSCRIPTION.reset',
  subscription: {
    accountId: ctx.accountId,
    pageInfo: ctx.pageInfo,
    creatorId: ctx.creatorId,
    userId: ctx.userId,
    leadType: ctx.type,
  },
});


// Machine definition
const leadAdMachine = (dispatch) => Machine<LeadAdContext, LeadAdSchema, LeadAdEvent>({
  id: 'lead-ads',
  initial: 'initializing',
  invoke: {
    id: 'lead-ad-subscriptions',
    src: subscriptionMachine,
  },
  states: {
    hidden: {
      on: {
        SHOW: 'disabled',
      },
    },
    initializing: {
      type: 'parallel',
      states: {
        accountId: {
          initial: 'awaiting',
          states: {
            awaiting: {
              on: {
                'ACCOUNT_ID.change': {
                  target: 'ready',
                  actions: assign((ctx, evt) => { ctx.accountId = evt.id; }),
                },
              },
            },
            ready: {
              type: 'final',
            },
          },
        },
        pageInfo: {
          initial: 'awaiting',
          states: {
            awaiting: {
              on: {
                'PAGE_INFO.change': {
                  target: 'ready',
                  actions: assign((ctx, evt) => { ctx.pageInfo = evt.pageInfo; }),
                },
              },
            },
            ready: {
              type: 'final',
            },
          },
        },
        creatorId: {
          initial: 'awaiting',
          states: {
            awaiting: {
              on: {
                'CREATOR_ID.change': {
                  target: 'ready',
                  actions: assign((ctx, evt) => { ctx.creatorId = evt.id; }),
                },
              },
            },
            ready: {
              type: 'final',
            },
          },
        },
        userId: {
          initial: 'awaiting',
          states: {
            awaiting: {
              on: {
                'USER_ID.change': {
                  target: 'ready',
                  actions: assign((ctx, evt) => { ctx.userId = evt.id; }),
                },
              },
            },
            ready: {
              type: 'final',
            },
          },
        },
      },
      onDone: 'disabled',
    },
    disabled: {
      id: 'disabled',
      entry: () => dispatch({ type: 'disableLeadAd' }),
      initial: 'checkingTosAccepted',
      on: {
        HIDE: {
          target: 'hidden',
          actions: [
            assign((ctx) => { ctx.selectedForm = null; }),
            () => dispatch({ type: 'disableLeadAd' }),
          ],
        },
      },
      states: {
        checkingTosAccepted: {
          invoke: {
            src: ctx => checkTosAccepted(ctx.pageInfo.id),
            onDone: [
              { target: 'needsTos', cond: (_, evt: DoneInvokeEvent<boolean>) => evt.data === false },
              { target: '#enabled', cond: ctx => ctx.enabledByDefault },
              { target: 'idle' },
            ],
            onError: {
              target: 'needsTos',
              actions: (_, evt) => {
                sendErrorService(new Error('Error determining lead ad tos accepted'), 'lead-ad-tos', evt.data);
                cogoToast.error('There was an issue determining if you accepted the Facebook lead ad terms of service');
              },
            },
          },
          exit: send(initSubscription, { to: 'lead-ad-subscriptions' }),
        },
        needsTos: {
          invoke: {
            id: 'lead-ad-tos',
            src: tosMachine,
            data: {
              pageId: (ctx: LeadAdContext) => ctx.pageInfo.id,
              interval: 2000,
            },
            onDone: '#enabled',
          },
          on: {
            'MODAL.open': {
              actions: forwardTo('lead-ad-tos'),
            },
          },
        },
        idle: {
          on: {
            TOGGLE_ENABLED: '#enabled',
          },
        },
      },
    },
    enabled: {
      id: 'enabled',
      entry: () => dispatch({ type: 'enableLeadAd' }),
      initial: 'loadingFormData',
      states: {
        loadingFormData: {
          on: {
            'FORMS.retrieved': [
              { target: 'formSelected', cond: ctx => !!ctx.selectedForm },
              { target: 'select' },
            ],
            'FORMS.noForms': {
              target: 'create.first',
            },
          },
        },
        select: {
          on: {
            TOGGLE_ENABLED: '#disabled.idle',
            'FORMS.select': {
              target: 'formSelected',
              actions: assign((ctx, evt) => { ctx.selectedForm = evt.id; }),
            },
            'FORM_MANAGER.toggleModes': 'create',
          },
        },
        formSelected: {
          entry: (ctx) => dispatch({ type: 'setSelectedForm', payload: ctx.selectedForm }),
          on: {
            TOGGLE_ENABLED: {
              target: '#disabled.idle',
              actions: [
                assign((ctx) => { ctx.selectedForm = null; }),
                () => dispatch({ type: 'setSelectedForm', payload: null }),
              ],
            },
            'FORMS.reset': {
              target: 'select',
              actions: [
                assign((ctx) => { ctx.selectedForm = null; }),
                () => dispatch({ type: 'setSelectedForm', payload: null }),
              ],
            },
          },
        },
        create: {
          initial: 'idle',
          on: {
            TOGGLE_ENABLED: '#disabled.idle',
            'FORMS.created': {
              target: 'formSelected',
              actions: assign((ctx, evt) => { ctx.selectedForm = evt.form.id; }),
            },
          },
          states: {
            idle: {
              on: {
                'FORM_MANAGER.toggleModes': '#enabled.select',
              },
            },
            first: {},
          },
        },
      },
    },
  },
  on: {
    'PAGE_INFO.change': {
      target: 'disabled',
      actions: assign((ctx, evt) => {
        ctx.pageInfo = evt.pageInfo;
        ctx.selectedForm = null;
      }),
    },
    'ACCOUNT_ID.change': {
      actions: assign((ctx, evt) => { ctx.accountId = evt.id; }),
    },
    'CREATOR_ID.change': {
      actions: assign((ctx, evt) => { ctx.creatorId = evt.id; }),
    },
    'TYPES.change': {
      actions: pure((_, evt) => [
        assign<LeadAdContext>((ctx) => { ctx.type = evt.leadType; }),
        send(resetSubscription, { to: 'lead-ad-subscriptions' }),
      ]),
    },
    'SOURCES.change': {
      actions: [
        assign((ctx, evt) => { ctx.source = evt.source; }),
        (_, evt) => dispatch({ type: 'setLeadSource', payload: evt.source }),
      ],
    },
    'SUBSCRIPTION.connected': {
      actions: assign((ctx, evt) => { ctx.type = evt.leadType || ctx.type; }),
    },
    'SUBSCRIPTION.notConnected': {
      actions: send(createSubscription, { to: 'lead-ad-subscriptions' }),
    },
  },
});

export default leadAdMachine;
